import AlternateEmail from "@mui/icons-material/AlternateEmail";
import Check from "@mui/icons-material/Check";
import CloudSync from "@mui/icons-material/CloudSync";
import Hiking from "@mui/icons-material/Hiking";
import MailOutline from "@mui/icons-material/MailOutline";
import OpenInNew from "@mui/icons-material/OpenInNew";
import PictureAsPdf from "@mui/icons-material/PictureAsPdf";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import Redo from "@mui/icons-material/Redo";
import Unpublished from "@mui/icons-material/Unpublished";
import Upload from "@mui/icons-material/Upload";
import Work from "@mui/icons-material/Work";
import { Box, Button, Chip, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { pink } from "@mui/material/colors";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  EditMode,
  defaultAddEditDocumentDialogState,
} from "components/Document";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { LongPollingJobWrapper, PollingResult } from "components/LongPollingJobWrapper";
import {
  CommunicationStatus,
  CommunicationType,
  DocumentQuery,
  DocumentType,
  JobStatus,
} from "generated-graphql/graphql";
import { useCheckForFile } from "hooks/useCheckForFile";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useNavigateToFulfillmentReports } from "hooks/useNavigateToFulfillmentReports";
import { useCallback, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { prettyPrintDateTime } from "util/dates";
import { downloadFromUrl, openInNewTabFromPromise } from "util/download";

import {
  useExemptMailingAddressFromVerification,
  useGenerateCombinedPdfFromDocuments,
  useRequeueCommunication,
  useSyncProofFromLob,
} from "../../api";
import { formatCommunicationType } from "../../utils";
import { useCommunication } from "../hooks/useCommunication";
import { useCommunicationFormContext } from "../hooks/useCommunicationForm";
import { useRecipientForm } from "../hooks/useRecipientForm";
import { DocumentItem } from "./DocumentItem";
import { StatusLabel } from "./StatusLabel";

export const PreviewRecipient = ({ index }: { index: number }) => {
  const alerts = useAlerts();
  const { watch, mode, control, getValues } = useCommunicationFormContext();
  const { communicationType } = useRecipientForm(index);
  const { communication } = useCommunication(getValues("id"));
  const [getDocumentDownloadLink] = useDocumentDownloadLink();
  const handleViewProof = useCallback(
    // "View Proof" means eyeball the mailed PDF, so it opens inline in a tab
    // (claimed synchronously — the signed URL only arrives after the query,
    // past the gesture window Safari's popup blocker enforces).
    async (proofDocumentId: string | undefined) => {
      if (proofDocumentId) {
        await openInNewTabFromPromise(async () => {
          const { data } = await getDocumentDownloadLink({
            variables: { id: proofDocumentId, inline: true },
          });
          return data?.getDocumentDownloadLink;
        });
      }
    },
    [getDocumentDownloadLink]
  );
  const { exemptMailingAddressFromVerification } = useExemptMailingAddressFromVerification();
  const { requeue, loading: requeueLoading } = useRequeueCommunication();
  const { syncProof, loading: syncProofLoading } = useSyncProofFromLob();

  const isView = mode === "view";
  const recipient = watch(`recipients.${index}`);
  const tenant = watch("tenant");
  const documents = watch("communicationDocuments");
  const coverLetter = watch(`recipients.${index}.coverLetterDocument`);
  const facilities = watch("facilities");

  const [addEditDialogState, setAddEditDialogState] = useState<AddEditDocumentDialogState>(
    defaultAddEditDocumentDialogState
  );

  const navigateToFulfillmentReports = useNavigateToFulfillmentReports({
    openInNewTab: true,
  });
  const navigate = useNavigate();
  const onClickGoToReport = useCallback(() => {
    if (communication?.tierIIReports) {
      navigateToFulfillmentReports(communication.tierIIReports.map((r) => r.id));
    }
  }, [communication?.tierIIReports, navigateToFulfillmentReports]);
  const onClickGoToSubmissions = useCallback(() => {
    const submissions = communication?.submissions ?? [];
    if (submissions.length === 1) {
      navigate(`/staff/fulfillment/waste/submissions/${submissions[0].id}`);
    } else if (submissions.length > 1) {
      window.open("/staff/fulfillment/waste", "_blank");
    }
  }, [communication?.submissions, navigate]);

  const { generateCombinedPdfFromDocuments, loading: generateCombinedPdfFromDocumentsLoading } =
    useGenerateCombinedPdfFromDocuments();

  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const onGenerateCombinedPdfFromDocumentsClick = useCallback(async () => {
    const documentIds = [coverLetter, ...documents]
      .filter((d): d is DocumentQuery["document"] => d !== null)
      .map((d) => d.id);

    if (documentIds.length === 0) {
      alerts.error("No documents to generate combined PDF from");
      return;
    }
    const result = await generateCombinedPdfFromDocuments(documentIds);
    setJobId(result?.id);
    alerts.info("Generating combined PDF. This may take a while, please wait...");
  }, [generateCombinedPdfFromDocuments, documents, coverLetter, alerts]);

  const { fetchFile } = useCheckForFile();
  const pollingCompleted = useCallback(
    async (result: PollingResult) => {
      setJobId(undefined);
      if (result.pollingTimedOut) {
        alerts.error("Generating combined PDF timed out. Please go check the Jobs dashboard :(");
        return;
      }
      if (result.job?.status === JobStatus.Succeeded) {
        const signedUrl = await fetchFile(result.job.output.outputKey);
        if (signedUrl) {
          downloadFromUrl(signedUrl);
        }
      }
    },
    [fetchFile, setJobId, alerts]
  );

  const addressLines = [
    recipient.mailRecipientAddressLine1,
    recipient.mailRecipientAddressLine2,
    recipient.mailRecipientCity,
  ]
    .filter(Boolean)
    .join(" ");

  const stateZip = [recipient.mailRecipientState, recipient.mailRecipientZip]
    .filter(Boolean)
    .join(" ");

  const fullAddress = [addressLines, stateZip].filter(Boolean).join(", ");

  const sent = communication?.status.includes("SENT");

  return (
    <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
      <Stack direction="row" spacing={3} flexGrow={1} minWidth={0}>
        <Box sx={{ p: 1 }}>
          <MailOutline />
        </Box>

        <Stack spacing={1} flexGrow={1} overflow="hidden" minWidth={0}>
          <Typography variant="subtitle1">{recipient.recipientName}</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {[tenant?.name, facilities.map((f) => f.name).join(", ")].join(" | ")}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {formatCommunicationType(communicationType)}
          </Typography>
          {communicationType === CommunicationType.Email && (
            <Stack spacing={2}>
              <Stack direction="row" gap={1} alignItems="center">
                <AlternateEmail color="primary" />
                <Stack direction="row" spacing={1}>
                  {recipient.emailRecipientAddresses?.map((email) => (
                    <Chip key={email} label={email} />
                  ))}
                </Stack>
              </Stack>
            </Stack>
          )}
          {communicationType !== CommunicationType.Email && (
            <Stack direction="row" gap={1} alignItems="center">
              <PlaceOutlined sx={{ color: pink[300] }} />
              <Typography>{fullAddress}</Typography>
            </Stack>
          )}
          <Controller
            name={`recipients.${index}.coverLetterDocument`}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) =>
              value || (!isView && !value) ? (
                <Stack spacing={2}>
                  <Box display="flex" justifyContent="flex-start">
                    {value && (
                      <>
                        <DocumentItem
                          document={value}
                          onRemove={isView ? undefined : () => onChange(null)}
                          removeButtonTitle="Remove cover letter"
                        />
                        <ErrorDisplay error={error} />
                      </>
                    )}

                    {!value && !isView && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Upload />}
                        disabled={!tenant}
                        onClick={() =>
                          setAddEditDialogState((state) => ({
                            ...state,
                            open: true,
                            mode: EditMode.ADD,
                            documentType: DocumentType.CoverLetter,
                          }))
                        }
                      >
                        Add Cover Letter
                      </Button>
                    )}
                  </Box>
                  {addEditDialogState.open && (
                    <AddEditDocumentDialog
                      dialogState={addEditDialogState}
                      setDialogState={setAddEditDialogState}
                      tenantId={tenant?.id ?? ""}
                      documentTypeOptions={[DocumentType.CoverLetter]}
                      onSubmit={(document) => {
                        onChange(document);
                        setAddEditDialogState(defaultAddEditDocumentDialogState);
                      }}
                      refetchQueries={[]}
                    />
                  )}
                </Stack>
              ) : (
                <></>
              )
            }
          />
          {documents.map((document) => (
            <DocumentItem key={document.id} document={document} />
          ))}
          {/* Only show button if there are documents or cover letter, idk why
          a cover letter would exist without attachments (required), but there's
          no reason to limit it */}
          {mode === "view" && (documents.length > 0 || coverLetter) && (
            <Box sx={{ display: "flex", alignItems: "flex-start", pt: 1 }}>
              <LongPollingJobWrapper
                jobId={jobId}
                pollingCompleted={pollingCompleted}
                timeoutInSeconds={600}
              >
                <Tooltip title="Download compiled pdf of all attachments">
                  <Button
                    onClick={onGenerateCombinedPdfFromDocumentsClick}
                    startIcon={<PictureAsPdf />}
                    disabled={generateCombinedPdfFromDocumentsLoading}
                    disableRipple
                    disableFocusRipple
                    sx={{
                      color: "primary.main",
                      padding: 0,
                      minWidth: 0,
                      overflow: "hidden",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        fontSize: "13px",
                        fontWeight: "500",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        minWidth: 0,
                      }}
                    >
                      Compiled PDF
                    </Typography>
                  </Button>
                </Tooltip>
              </LongPollingJobWrapper>
            </Box>
          )}
          <Box>
            {isView && sent && (
              <StatusLabel
                message={`Sent via
                    ${formatCommunicationType(communication?.communicationType)}`}
                icon={<Check fontSize="small" />}
              />
            )}
            {isView && communication?.status === CommunicationStatus.Error && (
              <StatusLabel
                variant="error"
                message={`Error: ${communication.job?.stepError || "Unknown error"}`}
              />
            )}
          </Box>
        </Stack>
      </Stack>

      {isView && communication && (
        <Stack gap={3}>
          <Stack textAlign="right" gap={1}>
            <Stack>
              {communication.completedByUser && (
                <Typography variant="caption">{communication.completedByUser.email}</Typography>
              )}
              {communication.completedAt && (
                <Typography variant="caption">
                  sent on {prettyPrintDateTime(communication.completedAt)}
                </Typography>
              )}
            </Stack>
          </Stack>
          <Divider />
          <Stack gap={1}>
            <Button
              startIcon={<OpenInNew />}
              sx={{ justifyContent: "flex-start" }}
              disabled={!communication.proofDocument}
              onClick={() => handleViewProof(communication.proofDocument?.id)}
            >
              View Proof
            </Button>
            <Button
              startIcon={<CloudSync />}
              sx={{ justifyContent: "flex-start" }}
              disabled={
                !!communication.proofDocument ||
                syncProofLoading ||
                communication.communicationType === CommunicationType.Email ||
                !communication.lobMail
              }
              onClick={() => communication.id && syncProof(communication.id)}
            >
              Sync Proof
            </Button>
            <Button
              startIcon={<Work />}
              sx={{ justifyContent: "flex-start" }}
              disabled={!communication.job}
              onClick={() => window.open(`/staff/jobs/${communication.jobId}`, "_blank")}
            >
              Go to Job
            </Button>
            <Button
              startIcon={<Hiking />}
              sx={{ justifyContent: "flex-start" }}
              disabled={!(communication.tierIIReports?.length ?? 0)}
              onClick={onClickGoToReport}
            >
              Go to Report
            </Button>
            {(communication.submissions?.length ?? 0) > 0 && (
              <Button
                startIcon={<OpenInNew />}
                sx={{ justifyContent: "flex-start" }}
                onClick={onClickGoToSubmissions}
              >
                {(communication.submissions?.length ?? 0) === 1
                  ? "View submission"
                  : "View submissions"}
              </Button>
            )}
            <Button
              startIcon={<Unpublished />}
              sx={{ justifyContent: "flex-start" }}
              disabled={
                communication.status !== CommunicationStatus.Error ||
                (communication.communicationType !== CommunicationType.CertifiedMail &&
                  communication.communicationType !== CommunicationType.FirstClassMail)
              }
              onClick={() => exemptMailingAddressFromVerification(communication)}
            >
              Exempt Mailing Address
            </Button>
            <Button
              startIcon={<Redo />}
              sx={{ justifyContent: "flex-start" }}
              disabled={requeueLoading || !!communication.proofDocument}
              onClick={() => requeue(communication)}
            >
              Resend
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
